import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import styles from './Toast.module.css';
import { cx } from '../../lib/cx';

export type ToastTone = 'info' | 'success' | 'error';

interface Toast {
  id: number;
  tone: ToastTone;
  message: ReactNode;
  duration: number;
}

interface ToastContextValue {
  show: (message: ReactNode, opts?: { tone?: ToastTone; duration?: number }) => void;
  success: (message: ReactNode, duration?: number) => void;
  error: (message: ReactNode, duration?: number) => void;
  info: (message: ReactNode, duration?: number) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const idRef = useRef(0);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const show = useCallback<ToastContextValue['show']>((message, opts) => {
    const id = ++idRef.current;
    const tone = opts?.tone ?? 'info';
    const duration = opts?.duration ?? 3500;
    setToasts((prev) => [...prev, { id, tone, message, duration }]);
    if (duration > 0) {
      window.setTimeout(() => dismiss(id), duration);
    }
  }, [dismiss]);

  const value = useMemo<ToastContextValue>(
    () => ({
      show,
      success: (m, d) => show(m, { tone: 'success', duration: d }),
      error: (m, d) => show(m, { tone: 'error', duration: d ?? 5000 }),
      info: (m, d) => show(m, { tone: 'info', duration: d }),
    }),
    [show],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      {createPortal(
        <div className={styles.container} aria-live="polite" aria-atomic="true">
          {toasts.map((t) => (
            <ToastItem key={t.id} toast={t} onClose={() => dismiss(t.id)} />
          ))}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}

function ToastItem({ toast, onClose }: { toast: Toast; onClose: () => void }) {
  useEffect(() => {
    // Focus-visible not needed; auto-dismiss handled by provider
  }, []);
  return (
    <div className={cx(styles.toast, styles[toast.tone])} role="status">
      <span className={styles.icon} aria-hidden />
      <div className={styles.body}>{toast.message}</div>
      <button type="button" className={styles.close} onClick={onClose} aria-label="Zavřít">
        ×
      </button>
    </div>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}
